/** A text-only snapshot: no editor HTML, links, external images or executable markup. */
export interface NoteTextStyle {
  readonly color: string;
  readonly background: string;
  readonly fontSize: number;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
}

export interface NoteTextRun extends NoteTextStyle {
  readonly text: string;
}

export type NoteParagraph = readonly NoteTextRun[];
export type NoteSection = readonly NoteParagraph[];

export const CLASSROOM_NOTE_FONT_SIZES = [13, 16, 18, 20, 22, 24, 28, 30, 32, 36, 40, 48] as const;

export const DEFAULT_NOTE_STYLE: NoteTextStyle = {
  color: '#152650',
  background: 'transparent',
  fontSize: 22,
  bold: false,
  italic: false,
  underline: false,
};

const BLOCK_TAGS = new Set([
  'DIV',
  'P',
  'LI',
  'UL',
  'OL',
  'H1',
  'H2',
  'H3',
  'H4',
  'BLOCKQUOTE',
  'PRE',
]);
const OMIT_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'IMG',
  'IFRAME',
  'OBJECT',
  'SVG',
  'CANVAS',
  'BUTTON',
]);

/** Empty lines separate panels; consecutive normal paragraphs stay in their original order. */
export function groupNoteParagraphs(paragraphs: readonly NoteParagraph[]): readonly NoteSection[] {
  const sections: NoteParagraph[][] = [];
  let current: NoteParagraph[] = [];
  for (const paragraph of paragraphs) {
    if (paragraph.some((run) => run.text.trim())) current.push(paragraph);
    else if (current.length > 0) {
      sections.push(current);
      current = [];
    }
  }
  if (current.length > 0) sections.push(current);
  return sections;
}

function resolvedNoteStyle(element: Element): NoteTextStyle {
  const style = getComputedStyle(element);
  // Highlight can be applied on an ancestor span while the text is nested in a bold span.
  let background =
    element.getAttribute('contenteditable') === 'true' ? 'transparent' : style.backgroundColor;
  let parent = element.parentElement;
  while (
    (!background || background === 'transparent' || background === 'rgba(0, 0, 0, 0)') &&
    parent
  ) {
    if (parent.getAttribute('contenteditable') === 'true') break;
    background = getComputedStyle(parent).backgroundColor;
    parent = parent.parentElement;
  }
  const size = Number.parseFloat(style.fontSize);
  return {
    color: style.color || DEFAULT_NOTE_STYLE.color,
    background: background || 'transparent',
    fontSize: Number.isFinite(size)
      ? Math.min(
          Math.max(...CLASSROOM_NOTE_FONT_SIZES),
          Math.max(CLASSROOM_NOTE_FONT_SIZES[0], size),
        )
      : DEFAULT_NOTE_STYLE.fontSize,
    bold: Number.parseInt(style.fontWeight, 10) >= 600 || style.fontWeight === 'bold',
    italic: style.fontStyle === 'italic',
    underline: style.textDecorationLine.includes('underline'),
  };
}

export function readClassroomNote(
  editor: HTMLElement,
  resolveStyle: (element: Element) => NoteTextStyle = resolvedNoteStyle,
): readonly NoteSection[] {
  const paragraphs: NoteTextRun[][] = [];
  let current: NoteTextRun[] = [];
  let characters = 0;
  const styles = new Map<Element, NoteTextStyle>();
  const flush = (allowEmpty = false) => {
    if (allowEmpty || current.length > 0) paragraphs.push(current);
    current = [];
  };
  const walk = (node: Node) => {
    if (node.nodeType === 3 && node.parentElement) {
      const text = (node.nodeValue ?? '').replace(/\u00a0/g, ' ');
      characters += text.length;
      if (characters > 8_000) throw new Error('笔记内容超过 8000 字，请精简后再分享。');
      let style = styles.get(node.parentElement);
      if (!style) {
        style = resolveStyle(node.parentElement);
        styles.set(node.parentElement, style);
      }
      const parts = text.replace(/\r\n?/g, '\n').split('\n');
      parts.forEach((part, index) => {
        if (index > 0) flush(true);
        if (part) current.push({ ...style, text: part });
      });
      return;
    }
    if (node.nodeType !== 1) return;
    const element = node as Element;
    if (OMIT_TAGS.has(element.tagName)) return;
    if (element.tagName === 'BR') {
      flush(true);
      return;
    }
    const block = BLOCK_TAGS.has(element.tagName);
    if (block) flush();
    for (const child of element.childNodes) walk(child);
    if (block) flush();
  };
  for (const child of editor.childNodes) walk(child);
  flush();
  return groupNoteParagraphs(paragraphs);
}

export function classroomNotePlainText(sections: readonly NoteSection[]): string {
  return sections
    .map((section) => section.map((line) => line.map((run) => run.text).join('')).join('\n'))
    .join('\n\n');
}

export function classroomNoteDate(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('.');
}

export function classroomNoteFilename(title: string, date: string, page: number): string {
  const safeTitle =
    title
      .replace(/[\\/:*?"<>|]/g, '')
      .split('')
      .filter((character) => character.charCodeAt(0) >= 32)
      .join('')
      .trim()
      .slice(0, 40) || '课堂笔记';
  return `${safeTitle}-${date}-${Math.max(1, Math.trunc(page))}.png`;
}
